import { BarChart3, Check, CheckCircle, ChevronDown, ChevronUp, Clock, Download, Edit, Eye, FileText, Info, Loader2, RefreshCw, Save, Search, Trash2, Upload, Users, Wand2, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import * as XLSX from '../../utils/xlsxCompat';
import api from '../../api';
import { useProject } from '../../contexts/ProjectContext';
import { useTerminology } from '../../contexts/TerminologyContext';
import { tokenStorage } from '../../utils/tokenStorage';
import { useOrchestratorContext } from '../../hooks/useOrchestratorContext';
import MergeTermsModal from '../MergeTermsModal';
import Tooltip from './Tooltip';
import ToolbarDropdown from '../ProcessMaps/ToolbarDropdown';
import HelpTooltip from '../HelpTooltip';

interface TerminologyAnalysisProps {
  analysisResults: any;
  onRefresh?: () => void;
}

interface ModernTermCardProps {
  term: any;
  editingTerm: number | null;
  editedDefinition: string;
  setEditedDefinition: (value: string) => void;
  editedTermName: string;
  setEditedTermName: (value: string) => void;
  editedContext: string;
  setEditedContext: (value: string) => void;
  onEdit: (term: any) => void;
  onSave: (termId: number) => void;
  onCancel: () => void;
  onApprove: (termId: number) => void;
  onDelete: (termId: number) => void;
  isSelected: boolean;
  onSelect: (termId: number) => void;
  isSaving: boolean;
}

const ModernTermCard: React.FC<ModernTermCardProps> = ({
  term,
  editingTerm,
  editedDefinition,
  setEditedDefinition,
  editedTermName,
  setEditedTermName,
  editedContext,
  setEditedContext,
  onEdit,
  onSave,
  onCancel,
  onApprove,
  onDelete,
  isSelected,
  onSelect,
  isSaving
}) => {
  const getStatusBadge = () => {
    if (term.is_approved || term.manual_approval_status === 'approved') {
      return (
        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-200 shadow-sm">
          <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full mr-2 shadow-sm"></div>
          Approved
        </span>
      );
    }
    if (term.requires_review) {
      return (
        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border border-amber-200 shadow-sm">
          <div className="w-2.5 h-2.5 bg-amber-500 rounded-full mr-2 shadow-sm animate-pulse"></div>
          Pending Review
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-slate-50 to-gray-50 text-slate-600 border border-slate-200 shadow-sm">
        <div className="w-2.5 h-2.5 bg-slate-400 rounded-full mr-2 shadow-sm"></div>
        Under Review
      </span>
    );
  };

  const getSourceName = (term: any) => {
    if (term.source_documents?.length === 1) return 'Document-Defined';
    if (term.source_documents?.length > 1) return 'Banking Database';
    if (!term.requires_review && term.is_acronym) return 'AI Resolved';
    if (term.requires_review) return 'Needs Review';
    return 'Unknown';
  };

  const getStatusName = (term: any) => {
    if (term.is_approved || term.manual_approval_status === 'approved') return 'Approved';
    if (term.requires_review) return 'Pending Review';
    return 'Under Review';
  };

  if (editingTerm === term.id) {
    return (
      <div className="group relative bg-gradient-to-br from-blue-50/30 via-white to-indigo-50/30 border-2 border-blue-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
        {/* Animated Edit Indicator */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 rounded-t-2xl animate-pulse"></div>

        {/* Editing Badge */}
        <div className="absolute -top-3 left-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg">
          <div className="flex items-center space-x-1.5">
            <Edit className="w-3 h-3" />
            <span>EDITING MODE</span>
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6 pt-4">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 border-2 border-blue-300 flex items-center justify-center shadow-lg">
              <Edit className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={editedTermName}
                onChange={(e) => setEditedTermName(e.target.value)}
                className="w-full text-xl font-bold text-gray-900 tracking-tight bg-white/80 border-2 border-blue-200 rounded-lg px-3 py-2 focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition-all duration-200 shadow-inner"
                placeholder="Enter term name..."
                aria-label="Term name"
              />
              <p className="text-sm text-blue-600 font-medium mt-1">Currently being edited</p>
            </div>
            {term.is_acronym && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-50 to-indigo-50 text-purple-700 border border-purple-200 shadow-sm">
                <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-1.5"></div>
                Acronym
              </span>
            )}
          </div>
          {getStatusBadge()}
        </div>

        {/* Enhanced Editing Form */}
        <div className="space-y-6">
          <div className="relative">
            <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">
              <span className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span>Term Definition</span>
              </span>
            </label>
            <div className="relative">
              <textarea
                value={editedDefinition}
                onChange={(e) => setEditedDefinition(e.target.value)}
                className="w-full p-6 border-2 border-blue-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-400 resize-none transition-all duration-200 bg-white/80 backdrop-blur-sm text-gray-800 font-medium leading-relaxed shadow-inner"
                rows={5}
                placeholder="Enter a clear, comprehensive definition for this term..."
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400 font-medium">
                {editedDefinition.length} characters
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-2 font-medium">
              ✨ Tip: Write a clear definition that anyone can understand
            </p>
          </div>

          <div className="relative">
            <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">
              <span className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span>Context</span>
              </span>
            </label>
            <div className="relative">
              <textarea
                value={editedContext}
                onChange={(e) => setEditedContext(e.target.value)}
                className="w-full p-6 border-2 border-blue-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-400 resize-none transition-all duration-200 bg-white/80 backdrop-blur-sm text-gray-800 font-medium leading-relaxed shadow-inner"
                rows={4}
                maxLength={2000}
                placeholder="Describe how this term is used in this process..."
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400 font-medium">
                {editedContext.length} characters
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-2 font-medium">
              ✨ Tip: Correct the extracted context if it is wrong — this is what informs the generated SOP
            </p>
          </div>

          <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 border border-green-200 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-700">Ready to approve this term?</p>
                  <p className="text-xs text-gray-500">Saving will mark this term as approved for documentation</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Tooltip
                  text="Cancel Editing Discard all changes and return to view mode without saving any modifications."
                  placement="bottom"
                >
                  <button
                    onClick={onCancel}
                    disabled={isSaving}
                    className="group/btn inline-flex items-center px-6 py-3 border-2 border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-100 transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    <span className="transition-transform group-hover/btn:scale-110">Cancel</span>
                  </button>
                </Tooltip>

                <Tooltip
                  text="Save & Approve Term Save the updated definition and automatically approve this term for use in all documentation and SOPs."
                  placement="bottom"
                >
                  <button
                    onClick={() => onSave(term.id)}
                    disabled={isSaving}
                    aria-busy={isSaving}
                    className="group/btn inline-flex items-center px-6 py-3 border-2 border-transparent text-sm font-bold rounded-xl text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-4 focus:ring-green-200 transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2 transition-transform group-hover/btn:scale-110" />
                        <span>Save & Approve</span>
                      </>
                    )}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`group relative bg-gradient-to-br from-white via-white to-gray-50/30 border rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${isSelected ? 'border-blue-500 bg-blue-50' :
      term.is_approved || term.manual_approval_status === 'approved'
        ? 'border-emerald-200 hover:border-emerald-300 hover:shadow-emerald-100/50'
        : term.requires_review
          ? 'border-amber-200 hover:border-amber-300 hover:shadow-amber-100/50'
          : 'border-slate-200 hover:border-slate-300 hover:shadow-slate-100/50'
      }`}>
      {/* Status Indicator Bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl ${isSelected ? 'bg-gradient-to-r from-blue-400 to-blue-500' :
        term.is_approved || term.manual_approval_status === 'approved'
          ? 'bg-gradient-to-r from-emerald-400 to-green-500'
          : term.requires_review
            ? 'bg-gradient-to-r from-amber-400 to-yellow-500'
            : 'bg-gradient-to-r from-slate-300 to-gray-400'
        }`}></div>

      {/* Header Section */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center space-x-4">
          {/* Selection Checkbox */}
          <button
            onClick={() => onSelect(term.id)}
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${isSelected
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'border-gray-300 hover:border-blue-400'
              }`}
          >
            {isSelected && <Check className="w-3 h-3" />}
          </button>

          {/* Info icon removed as per requirement */}

          <div className="flex flex-col">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-xl font-bold text-gray-900 tracking-tight">{term.term}</h3>
              {term.is_acronym && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-50 to-indigo-50 text-purple-700 border border-purple-200 shadow-sm">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-1.5"></div>
                  Acronym
                </span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${(term.confidence_score || 0) >= 90 ? 'bg-emerald-500' :
                  (term.confidence_score || 0) >= 70 ? 'bg-blue-500' :
                    (term.confidence_score || 0) >= 50 ? 'bg-amber-500' : 'bg-red-500'
                  }`}></div>
                <span className="text-sm font-medium text-gray-600">
                  {term.confidence_score || 0}% confidence
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {getStatusBadge()}

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            {/* Approve Button */}
            {!(term.is_approved || term.manual_approval_status === 'approved') && (
              <Tooltip
                text={`Approve Term
                  Click to approve this term for use in documentation.
                  • Marks term as ready for SOPs
                  • Includes in official glossary
                  • No further review needed`}
                placement="bottom"
              >
                <button
                  onClick={() => onApprove(term.id)}
                  className="group/btn relative inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 text-green-600 hover:from-green-100 hover:to-emerald-100 hover:border-green-300 hover:text-green-700 transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-green-200/50"
                >
                  <Check className="w-4 h-4 transition-transform group-hover/btn:scale-110" />
                </button>
              </Tooltip>
            )}

            {/* Edit Button */}
            <Tooltip
              text={`Edit Term Definition
                Click to edit this term's definition and review status.
                • Update definition for clarity
                • Mark as approved/pending
                • Add context and notes`}
              placement="bottom"
            >
              <button
                onClick={() => onEdit(term)}
                className="group/btn relative inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 text-blue-600 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 hover:text-blue-700 transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-blue-200/50"
              >
                <Edit className="w-4 h-4 transition-transform group-hover/btn:scale-110" />
              </button>
            </Tooltip>

            {/* Delete Button */}
            <Tooltip
              text={`Delete Term
              Click to remove this term from the glossary.
              • Permanently removes term
              • Cannot be undone
              • Use with caution`}
              placement="bottom"
            >
              <button
                onClick={() => onDelete(term.id)}
                className="group/btn relative inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 text-red-600 hover:from-red-100 hover:to-rose-100 hover:border-red-300 hover:text-red-700 transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-red-200/50"
              >
                <Trash2 className="w-4 h-4 transition-transform group-hover/btn:scale-110" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Definition Section */}
      <div className="mb-6">
        <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Definition</h4>
          <p className="text-gray-800 leading-relaxed font-medium">
            {term.definition}
          </p>
        </div>
      </div>

      {/* Context Section */}
      {term.context && (
        <div className="mb-6">
          <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
            <h4 className="text-sm font-semibold text-blue-700 mb-2 uppercase tracking-wide">Context</h4>
            <p className="text-blue-800 leading-relaxed">
              {term.context}
            </p>
          </div>
        </div>
      )}

      {/* Metadata Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center space-x-6">
          {/* Hide "Used X times" as per client request */}
          {false && <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500"></div>
            <span className="text-sm font-semibold text-gray-700">
              Used {term.total_usage || 0} {(term.total_usage || 0) === 1 ? 'time' : 'times'}
            </span>
          </div>}

          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-500"></div>
            <span className="text-sm font-semibold text-gray-700">
              {getSourceName(term)}
            </span>
          </div>
        </div>

        <div className="text-xs text-gray-400 font-medium">
          ID: {term.id}
        </div>
      </div>
    </div>
  );
};

const TerminologyAnalysis: React.FC<TerminologyAnalysisProps> = ({ analysisResults, onRefresh }) => {
  // Use context data instead of local state for persistence
  const { fullTerminologyData, analysisLoading, loadFullTerminology, setFullTerminologyData } = useTerminology();
  const [localAnalysisLoading, setLocalAnalysisLoading] = useState(false);
  const [editingTerm, setEditingTerm] = useState<number | null>(null);
  const [editedDefinition, setEditedDefinition] = useState('');
  const [editedTermName, setEditedTermName] = useState('');
  const [editedContext, setEditedContext] = useState('');
  const [isSavingTerm, setIsSavingTerm] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [sortField, setSortField] = useState<string>('term');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTerms, setSelectedTerms] = useState<Set<number>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [realTimeStats, setRealTimeStats] = useState({
    uniqueTerms: 0,
    termsMatched: 0,
    autoApproved: 0,
    needsReview: 0
  });
  const [showRealTimeLog, setShowRealTimeLog] = useState(false);
  interface RealTimeLogEntry {
    id: number;
    timestamp: string;
    type: string;
    message: string;
  }
  const [realtimeLog, setRealtimeLog] = useState<RealTimeLogEntry[]>([]);
  const [matchingInProgress, setMatchingInProgress] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const { currentProject } = useProject();
  const orchestratorCtx = useOrchestratorContext();
  // In Smart Router, use the session's project ID to avoid wrong-project API calls
  const effectiveProjectId = orchestratorCtx.inRouter && orchestratorCtx.extraParams?.projectId
    ? parseInt(orchestratorCtx.extraParams.projectId, 10)
    : currentProject?.id || 0;
  const { refreshTerminology } = useTerminology();

  // Removed local loadEnhancedTerminology - now using context version

  // No need for useEffect - context handles loading automatically when project changes

  const handleAnalyzeTerminology = async () => {
    if (!currentProject?.id || !analysisResults?.document_analyses?.length) return;

    setLocalAnalysisLoading(true);
    setRealTimeStats({ uniqueTerms: 0, termsMatched: 0, autoApproved: 0, needsReview: 0 });
    setRealtimeLog([]);

    try {
      startSSEConnection(currentProject.id);

      const response = await Promise.race([
        api.post(`/api/terminology/${currentProject.id}/analyze`, {
          realTimeUpdates: true,
          autoMatch: true
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        )
      ]);

      //@ts-expect-error
      if (response.data.data?.real_time_updates) {
        console.log('Real-time analysis started successfully');

        setRealtimeLog(prev => [...prev.slice(-49), {
          id: Date.now(),
          timestamp: new Date().toLocaleTimeString(),
          type: 'system',
          message: 'Analysis started - waiting for updates...'
        }]);

      } else {

        toast.success('Terminology analysis completed');
        await loadFullTerminology();
        setLocalAnalysisLoading(false);
      }

    } catch (error) {
      console.error('Error analyzing terminology:', error);

      //@ts-expect-error
      if (error.message === 'Request timeout') {
        setRealtimeLog(prev => [...prev.slice(-49), {
          id: Date.now(),
          timestamp: new Date().toLocaleTimeString(),
          type: 'system',
          message: 'API request timed out, but analysis may still be running in background...'
        }]);

      } else {
        toast.error('Failed to start terminology analysis');
        setLocalAnalysisLoading(false);
        stopSSEConnection();
      }
    }
  };

  const checkAnalysisCompletion = useCallback(async () => {
    if (!currentProject?.id || !localAnalysisLoading) return;

    try {
      // Check if new terminology data is available (indicating analysis completed)
      const response = await api.get(`/api/terminology/${currentProject.id}`);
      const newTermsCount = response.data.data.terms.length;

      // If we have significantly more terms than before, analysis likely completed
      const currentTermsCount = fullTerminologyData?.enhancedTerms?.length || 0;

      if (newTermsCount > currentTermsCount) {
        console.log(`🎉 Analysis appears to have completed (detected ${newTermsCount} terms, was ${currentTermsCount})`);

        setRealtimeLog(prev => [...prev.slice(-49), {
          id: Date.now(),
          timestamp: new Date().toLocaleTimeString(),
          type: 'system',
          message: `Analysis completed! ${newTermsCount} unique terms in database (${newTermsCount - currentTermsCount} new).`
        }]);

        // ✅ FIX: Update final stats before completing
        setRealTimeStats(prev => ({
          ...prev,
          uniqueTerms: newTermsCount,
        }));

        // Load the new data
        await loadFullTerminology();
        toast.success(`Terminology analysis completed! Added ${newTermsCount - currentTermsCount} new unique terms.`);
        setLocalAnalysisLoading(false);
        setMatchingInProgress(false);
        stopSSEConnection();
      }
    } catch (error) {
      console.error('Error checking analysis completion:', error);
    }
  }, [currentProject?.id, localAnalysisLoading, fullTerminologyData?.enhancedTerms?.length, loadFullTerminology]);

  useEffect(() => {
    if (!localAnalysisLoading) return;

    let attempts = 0;
    const maxAttempts = 36;

    const fallbackInterval = setInterval(() => {
      attempts++;
      const interval = attempts <= 12 ? 5000 : 10000;

      console.log(`🔍 Checking analysis completion (fallback attempt ${attempts}/${maxAttempts})...`);
      checkAnalysisCompletion();

      if (attempts >= maxAttempts) {
        console.log('⏰ Fallback polling timeout - analysis may still be running');
        clearInterval(fallbackInterval);

        setRealtimeLog(prev => [...prev.slice(-49), {
          id: Date.now(),
          timestamp: new Date().toLocaleTimeString(),
          type: 'warning',
          message: 'Analysis taking longer than expected - please check back or refresh the page.'
        }]);
      }
    }, attempts <= 12 ? 5000 : 10000);

    return () => {
      clearInterval(fallbackInterval);
    };
  }, [localAnalysisLoading, checkAnalysisCompletion]);

  useEffect(() => {
    if (!localAnalysisLoading) return;

    const fallbackInterval = setInterval(() => {
      console.log('🔍 Checking analysis completion (fallback)...');
      checkAnalysisCompletion();
    }, 10000);

    return () => {
      clearInterval(fallbackInterval);
    };
  }, [localAnalysisLoading, checkAnalysisCompletion]);


  const handleEditTerm = (term: any) => {
    setEditingTerm(term.id);
    setEditedDefinition(term.definition);
    setEditedTermName(term.term || '');
    setEditedContext(term.context || '');
  };

  const handleSaveTerm = async (termId: number) => {
    if (!currentProject?.id) return;
    if (isSavingTerm) return;

    const trimmedName = (editedTermName || '').trim();
    if (!trimmedName) {
      toast.error('Term name cannot be empty');
      return;
    }

    setIsSavingTerm(true);
    const savingToast = toast.loading('Saving term...');
    try {
      await api.put(`/api/terminology/${currentProject.id}/terms/${termId}`, {
        term: trimmedName,
        definition: editedDefinition,
        context: editedContext,
        is_approved: true,
        requires_review: false
      });

      // Wait for the list refresh + tooltip context refresh BEFORE
      // closing edit mode and showing success — otherwise the user sees
      // stale (pre-edit) card data for a second while the new data
      // arrives, which feels like the update didn't apply.
      // The save itself already succeeded; refresh failures must not flip
      // the success toast to an error.
      try {
        await Promise.all([
          loadFullTerminology(),
          refreshTerminology(),
        ]);
        if (onRefresh) {
          await onRefresh();
        }
      } catch (refreshError) {
        console.warn('Post-save refresh failed:', refreshError);
      }

      toast.dismiss(savingToast);
      toast.success('Term updated successfully');
      setEditingTerm(null);
      setEditedTermName('');
      setEditedContext('');
    } catch (error: any) {
      console.error('Error updating term:', error);
      toast.dismiss(savingToast);
      const status = error?.response?.status;
      const serverMsg = error?.response?.data?.message || error?.response?.data?.error;
      if (status === 409) {
        toast.error(serverMsg || 'A term with this name already exists. Please choose a different name.', { duration: 6000 });
      } else {
        toast.error(serverMsg || 'Failed to update term');
      }
    } finally {
      setIsSavingTerm(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingTerm(null);
    setEditedDefinition('');
    setEditedTermName('');
    setEditedContext('');
  };

  const handleApproveTerm = async (termId: number) => {
    if (!currentProject?.id) return;

    try {
      await api.put(`/api/terminology/${currentProject.id}/terms/${termId}`, {
        is_approved: true,
        requires_review: false
      });

      toast.success('Term approved successfully');
      loadFullTerminology();

      await refreshTerminology();

      // Trigger process synthesis refresh
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error approving term:', error);
      toast.error('Failed to approve term');
    }
  };

  const handleDeleteTerm = async (termId: number) => {
    if (!currentProject?.id) return;

    if (!window.confirm('Are you sure you want to delete this term? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/api/terminology/${currentProject.id}/terms/${termId}`);
      toast.success('Term deleted successfully');
      loadFullTerminology();
    } catch (error) {
      console.error('Error deleting term:', error);
      toast.error('Failed to delete term');
    }
  };

  const handleSelectTerm = (termId: number) => {
    const newSelected = new Set(selectedTerms);
    if (newSelected.has(termId)) {
      newSelected.delete(termId);
    } else {
      newSelected.add(termId);
    }
    setSelectedTerms(newSelected);
  };

  const handleSelectAll = () => {
    const filteredTermIds = getFilteredAndSortedTerms(fullTerminologyData?.enhancedTerms || []).map(t => t.id);
    if (selectedTerms.size === filteredTermIds.length) {
      setSelectedTerms(new Set());
    } else {
      setSelectedTerms(new Set(filteredTermIds));
    }
  };

  const handleMergeTerms = () => {
    if (selectedTerms.size < 2) {
      toast.error('Please select at least 2 terms to merge');
      return;
    }
    setIsMergeModalOpen(true);
  };

  const handleMergeComplete = () => {
    loadFullTerminology();
    setSelectedTerms(new Set());
    setIsMergeModalOpen(false);
  };

  const getSelectedTermsData = () => {
    return (fullTerminologyData?.enhancedTerms || []).filter((term: any) => selectedTerms.has(term.id));
  };

  const handleBulkApprove = async () => {
    if (!currentProject?.id || selectedTerms.size === 0) return;

    setBulkActionLoading(true);
    try {
      await api.post(`/api/terminology/${currentProject.id}/bulk-approve`, {
        termIds: Array.from(selectedTerms)
      });

      toast.success(`Approved ${selectedTerms.size} terms`);
      setSelectedTerms(new Set());
      loadFullTerminology();

      // Refresh terminology context for tooltips
      await refreshTerminology();

      // Trigger process synthesis refresh
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error bulk approving terms:', error);
      toast.error('Failed to approve terms');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!currentProject?.id || selectedTerms.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedTerms.size} terms? This action cannot be undone.`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      await api.post(`/api/terminology/${currentProject.id}/bulk-delete`, {
        termIds: Array.from(selectedTerms)
      });

      toast.success(`Deleted ${selectedTerms.size} terms`);
      setSelectedTerms(new Set());
      loadFullTerminology();
    } catch (error) {
      console.error('Error bulk deleting terms:', error);
      toast.error('Failed to delete terms');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const deduplicateTerms = (terms: any[]) => {
    const seen = new Set();
    return terms.filter(term => {
      const key = term.term?.toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  const handleDownloadTerminology = async (format: 'csv' | 'xlsx' = 'csv') => {
    if (!currentProject?.id || !fullTerminologyData) return;

    setIsDownloading(true);
    try {
      if (format === 'csv') {
        // Create a formatted CSV content
        const csvContent = generateTerminologyCSV(fullTerminologyData);

        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `terminology-${currentProject.name || 'project'}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Create Excel file
        const workbook = generateTerminologyExcel(fullTerminologyData);
        const excelBuffer = await XLSX.write(workbook);

        // Create and download the file
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `terminology-${currentProject.name || 'project'}-${new Date().toISOString().split('T')[0]}.xlsx`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      toast.success('Terminology downloaded successfully');
    } catch (error) {
      console.error('Error downloading terminology:', error);
      toast.error('Failed to download terminology');
    } finally {
      setIsDownloading(false);
    }
  };

  const generateTerminologyCSV = (terminologyData: any) => {
    if (!terminologyData) return '';
    const headers = ['Term', 'Definition', 'Source', 'Complexity Level', 'Confidence Score', 'Status', 'Context', 'Usage Count'];
    const rows = [headers.join(',')];

    if (terminologyData.enhancedTerms) {
      terminologyData.enhancedTerms.forEach((term: any) => {
        const getSourceName = (term: any) => {
          if (term.source_documents?.length === 1) return 'Document-Defined';
          if (term.source_documents?.length > 1) return 'Banking Database';
          if (!term.requires_review && term.is_acronym) return 'AI Resolved';
          if (term.requires_review) return 'Needs Review';
          return 'Unknown';
        };

        const getStatusName = (term: any) => {
          if (term.is_approved || term.manual_approval_status === 'approved') return 'Approved';
          if (term.requires_review) return 'Pending Review';
          return 'Under Review';
        };

        const row = [
          `"${term.term || ''}"`,
          `"${(term.definition || '').replace(/"/g, '""')}"`,
          `"${getSourceName(term)}"`,
          `"${term.complexity_level || 'N/A'}"`,
          `"${term.confidence_score || 0}%"`,
          `"${getStatusName(term)}"`,
          `"${(term.context || '').replace(/"/g, '""')}"`,
          `"${term.total_usage || 0}"`
        ];
        rows.push(row.join(','));
      });
    }

    return rows.join('\n');
  };

  const generateTerminologyExcel = (terminologyData: any) => {
    if (!terminologyData) return XLSX.utils.book_new();
    const headers = ['Term', 'Definition', 'Source', 'Complexity Level', 'Confidence Score', 'Status', 'Context', 'Usage Count'];
    const data: any[][] = [headers];

    if (terminologyData.enhancedTerms) {
      terminologyData.enhancedTerms.forEach((term: any) => {
        const getSourceName = (term: any) => {
          if (term.source_documents?.length === 1) return 'Document-Defined';
          if (term.source_documents?.length > 1) return 'Banking Database';
          if (!term.requires_review && term.is_acronym) return 'AI Resolved';
          if (term.requires_review) return 'Needs Review';
          return 'Unknown';
        };

        const getStatusName = (term: any) => {
          if (term.is_approved || term.manual_approval_status === 'approved') return 'Approved';
          if (term.requires_review) return 'Pending Review';
          return 'Under Review';
        };

        const row = [
          term.term || '',
          term.definition || '',
          getSourceName(term),
          term.complexity_level || 'N/A',
          `${term.confidence_score || 0}%`,
          getStatusName(term),
          term.context || '',
          term.total_usage || 0
        ];
        data.push(row);
      });
    }

    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Terminology');

    return workbook;
  };

  const handleUploadTerminology = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const workbook = await XLSX.readAsync(e.target?.result as ArrayBuffer);
        const worksheet = workbook.sheets[0];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Process and upload the data
        await api.post(`/api/terminology/${currentProject?.id}/upload`, {
          terms: jsonData
        });

        toast.success('Terminology uploaded successfully');
        loadFullTerminology();
      } catch (error) {
        console.error('Error uploading terminology:', error);
        toast.error('Failed to upload terminology');
      } finally {
        setIsUploading(false);
      }
    };

    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  const getFilteredAndSortedTerms = (terms: any[]) => {
    if (!terms) return [];

    let filtered = [...terms];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(term =>
        term.term?.toLowerCase().includes(search) ||
        term.definition?.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'approved':
          filtered = filtered.filter(term => term.is_approved || term.manual_approval_status === 'approved');
          break;
        case 'pending':
          filtered = filtered.filter(term => term.requires_review);
          break;
        case 'review':
          filtered = filtered.filter(term => !term.is_approved && !term.requires_review);
          break;
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'term':
          aVal = a.term?.toLowerCase() || '';
          bVal = b.term?.toLowerCase() || '';
          break;
        case 'confidence':
          aVal = a.confidence_score || 0;
          bVal = b.confidence_score || 0;
          break;
        case 'status':
          aVal = a.is_approved || a.manual_approval_status === 'approved' ? 2 : a.requires_review ? 1 : 0;
          bVal = b.is_approved || b.manual_approval_status === 'approved' ? 2 : b.requires_review ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return filtered;
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const startSSEConnection = (projectId: number) => {
    // SECURITY FIX: Always close existing connection to prevent cross-project data leakage
    if (eventSourceRef.current) {
      console.log('Closing existing SSE connection before starting new one');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Store the project ID this connection is for
    const connectionProjectId = projectId;

    try {
      const token = tokenStorage.getAccessToken();
      if (!token) {
        console.error('No authentication token found for SSE connection');
        setRealtimeLog(prev => [...prev.slice(-49), {
          id: Date.now(),
          timestamp: new Date().toLocaleTimeString(),
          type: 'error',
          message: 'Authentication required - please refresh the page'
        }]);
        return;
      }

      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
      const sseURL = `${baseURL}/api/terminology/${projectId}/analysis-stream?token=${encodeURIComponent(token)}`;

      console.log(`Starting SSE connection to: ${sseURL.replace(/token=[^&]+/, 'token=***')}`);

      const eventSource = new EventSource(sseURL);
      eventSourceRef.current = eventSource;

      // Connection opened
      eventSource.onopen = (event) => {
        console.log('SSE connection established for terminology analysis');
        setRealtimeLog(prev => [...prev.slice(-49), {
          id: Date.now(),
          timestamp: new Date().toLocaleTimeString(),
          type: 'system',
          message: 'Real-time connection established'
        }]);
      };

      // Message received
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // SECURITY CHECK: Verify the message is for the current project
          // This prevents displaying data from previous projects
          if (data.projectId && data.projectId !== connectionProjectId) {
            console.warn(`Received SSE message for project ${data.projectId} but current connection is for ${connectionProjectId}. Ignoring.`);
            return;
          }

          // Additional safety: Check against the current project in component state
          if (currentProject?.id && connectionProjectId !== currentProject.id) {
            console.warn(`SSE connection is for project ${connectionProjectId} but current project is ${currentProject.id}. Closing stale connection.`);
            eventSource.close();
            return;
          }

          // Handle different message types
          if (data.type === 'connected') {
            console.log('SSE connected:', data.message);
            return;
          }

          if (data.type === 'heartbeat') {
            return;
          }

          handleRealTimeUpdate(data);
        } catch (error) {
          console.error('Error parsing SSE data:', error);
          setRealtimeLog(prev => [...prev.slice(-49), {
            id: Date.now(),
            timestamp: new Date().toLocaleTimeString(),
            type: 'error',
            message: 'Failed to parse server update'
          }]);
        }
      };

      // Connection error
      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);

        if (eventSource.readyState === EventSource.CLOSED) {
          console.log('SSE connection closed by server');

          setRealtimeLog(prev => [...prev.slice(-49), {
            id: Date.now(),
            timestamp: new Date().toLocaleTimeString(),
            type: 'error',
            message: 'Connection lost - analysis may continue in background'
          }]);

          eventSource.close();
        }
      };

    } catch (error) {
      console.error('Failed to start SSE connection:', error);
      setRealtimeLog(prev => [...prev.slice(-49), {
        id: Date.now(),
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      }]);
    }
  };

  const stopSSEConnection = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;

      setRealtimeLog(prev => [...prev.slice(-49), {
        id: Date.now(),
        timestamp: new Date().toLocaleTimeString(),
        type: 'system',
        message: 'Real-time connection closed'
      }]);
    }
  };



  const handleRealTimeUpdate = (data: { type: string; message: string; details?: any, source?: string }) => {
    const { type, message, details, source } = data;

    const actualType = type.startsWith('terminology_') ? type.replace('terminology_', '') : type;
    const cleanMessage = message.startsWith('[Terminology] ') ? message.replace('[Terminology] ', '') : message;

    if (source === 'terminology_broadcast' && !localAnalysisLoading) {
      console.log('🔄 Received terminology broadcast - starting to show updates');
      setLocalAnalysisLoading(true);
      setRealTimeStats({
        uniqueTerms: 0,
        termsMatched: 0,
        autoApproved: 0,
        needsReview: 0
      });
      setRealtimeLog([]);
    }


    setRealtimeLog(prev => [...prev.slice(-49), {
      id: Date.now() + Math.random(),
      timestamp: new Date().toLocaleTimeString(),
      type: actualType,
      message: cleanMessage
    }]);

    // Update stats based on type
    switch (actualType) {
      case 'analysis_start':
        setRealTimeStats(prev => ({
          ...prev,
          termsFound: 0,
          termsMatched: 0,
          autoApproved: 0,
          needsReview: 0
        }));
        break;

      case 'documents_found':
        console.log(`📁 Found ${details?.total || 0} documents to process`);
        break;

      case 'document_processing':
        console.log(`📄 Processing: ${details?.document_name} (${details?.processed}/${details?.total})`);
        break;

      case 'terms_found':
        setRealTimeStats(prev => ({
          ...prev,
          uniqueTerms: details?.totalUnique || prev.uniqueTerms
        }));
        console.log(`📝 ${details?.document}: ${details?.newUnique} new unique terms found. Total: ${details?.totalUnique}`);
        break;
        
      case 'extraction_summary':
        setRealTimeStats(prev => ({
          ...prev,
          uniqueTerms: details?.totalUnique || prev.uniqueTerms
        }));
        console.log(`📊 Analysis complete: ${details?.totalUnique} total unique terms (${details?.newTerms} new)`);
        break;

      case 'template_matching_start':
        setMatchingInProgress(true);
        setRealTimeStats(prev => ({
          ...prev,
          termsMatched: 0,
          autoApproved: 0,
          needsReview: 0
        }));
        console.log('🔄 Template matching started');
        break;

      case 'template_matching_progress':
        console.log(`🔄 Template matching progress: ${message}`);
        break;

      case 'term_matched':
        setRealTimeStats(prev => ({
          ...prev,
          termsMatched: prev.termsMatched + 1,
          autoApproved: details?.auto_approved ? prev.autoApproved + 1 : prev.autoApproved,
          needsReview: details?.auto_approved ? prev.needsReview : prev.needsReview + 1
        }));
        console.log(`✅ Matched term: ${details?.term} → ${details?.matched_with} (${details?.score}%)`);
        break;

      case 'template_matching_complete':
        setMatchingInProgress(false);
        if (details) {
          setRealTimeStats(prev => ({
            ...prev,
            autoApproved: details.auto_approved || prev.autoApproved,
            needsReview: details.needs_review || prev.needsReview
          }));
        }

        console.log('⏳ Waiting for final analysis_complete signal...');
        break;

      case 'analysis_complete':
        console.log('🎊 Analysis completed successfully!');
        setMatchingInProgress(false);

        // ✅ FIX: Only reload and cleanup AFTER analysis_complete
        setTimeout(async () => {
          try {
            await loadFullTerminology();
            toast.success('Terminology analysis completed successfully!');
          } catch (error) {
            console.error('Error reloading terminology data:', error);
            toast.error('Analysis completed but failed to reload data');
          } finally {
            setLocalAnalysisLoading(false);
            // stopSSEConnection();
          }
        }, source === 'terminology_broadcast' ? 500 : 1000);
        break;

      case 'error':
        console.error(`❌ Analysis error: ${message}`);
        setMatchingInProgress(false);
        setLocalAnalysisLoading(false);
        toast.error(`Analysis error: ${message}`);
        if (source !== 'terminology_broadcast') {
          stopSSEConnection();
        }
        break;
        break;

      case 'warning':
        console.warn(`⚠️ Analysis warning: ${message}`);
        // ✅ FIX: Don't close connection on warnings - continue processing
        break;

      case 'connected':
        console.log('🔗 SSE connected successfully');
        break;

      case 'heartbeat':
        // Silent heartbeat - don't log to console
        break;

      default:
        console.log(`❓ Unhandled SSE message type: ${type} - ${message}`);
        break;
    }
  };

  // CRITICAL FIX: Clear activity log and close SSE when project changes
  useEffect(() => {
    // When project changes, clear all project-specific data
    if (currentProject?.id) {
      // Close any existing SSE connection from previous project
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      
      // Clear the activity log to prevent data leakage between projects
      setRealtimeLog([]);
      
      // Reset real-time statistics
      setRealTimeStats({
        uniqueTerms: 0,
        termsMatched: 0,
        autoApproved: 0,
        needsReview: 0
      });
      
      // Reset other project-specific states
      setMatchingInProgress(false);
      setShowRealTimeLog(false);
      
      console.log(`Project changed to ${currentProject.id} - cleared activity log and reset states`);
    }
  }, [currentProject?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);


  const checkForPendingUpdates = useCallback(async () => {
    if (!currentProject?.id) return;

    try {
      const response = await api.get(`/api/terminology/${currentProject.id}/pending-updates`);
      const pendingUpdates = response.data.data.updates || [];

      if (pendingUpdates.length > 0) {
        console.log(`📥 Found ${pendingUpdates.length} pending terminology updates`);

        // Process each pending update
        pendingUpdates.forEach((update: any) => {
          console.log(`📝 Processing pending update: ${update.type} - ${update.message}`);
          handleRealTimeUpdate({
            type: update.type,
            message: update.message,
            details: update.details,
            source: 'pending_updates'
          });
        });

        setRealtimeLog(prev => [...prev.slice(-49), {
          id: Date.now(),
          timestamp: new Date().toLocaleTimeString(),
          type: 'system',
          message: `Retrieved ${pendingUpdates.length} missed updates from background analysis.`
        }]);

        // If any updates indicate completion, refresh the data
        const hasCompletion = pendingUpdates.some((update: any) =>
          update.type === 'analysis_complete' || update.type === 'template_matching_complete'
        );

        if (hasCompletion) {
          console.log('🔄 Pending updates indicate analysis completion - refreshing data');
          await loadFullTerminology();
          toast.success(`Analysis completed in background! Found ${pendingUpdates.length} updates.`);
        }
      }
    } catch (error) {
      console.error('Error checking for pending updates:', error);
    }
  }, [currentProject?.id, handleRealTimeUpdate, loadFullTerminology]);

  // Add this useEffect to check for pending updates when component mounts or project changes
  useEffect(() => {
    if (currentProject?.id) {
      // Check for pending updates when component loads
      checkForPendingUpdates();

      // Also check periodically in case user switches tabs during analysis
      const pendingCheckInterval = setInterval(() => {
        if (!localAnalysisLoading) { // Only check when not actively analyzing
          checkForPendingUpdates();
        }
      }, 15000); // Check every 15 seconds

      return () => {
        clearInterval(pendingCheckInterval);
      };
    }
  }, [currentProject?.id, checkForPendingUpdates, localAnalysisLoading]);


  if (!analysisResults?.document_analyses?.length) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Analyzed</h3>
        <p className="text-gray-500">
          Please analyze some documents first to extract terminology data.
        </p>
      </div>
    );
  }

  // Show loading state when initially fetching terminology data (not during analysis)
  if (analysisLoading && !fullTerminologyData) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <RefreshCw className="w-10 h-10 text-indigo-600 animate-spin" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Loading Terminology</h3>
          <p className="text-gray-600">
            Fetching saved terminology data from database...
          </p>
        </div>
      </div>
    );
  }

  if (!fullTerminologyData || (fullTerminologyData && fullTerminologyData.enhancedTerms?.length === 0)) {
    const hasSynthesis = !!analysisResults?.process_synthesis;

    // Show loader ONLY when process synthesis is complete (terminology should be
    // generating) OR if user manually triggered analysis. Terminology is extracted
    // from source documents right after synthesis — it does not depend on the SOP.
    if (hasSynthesis || localAnalysisLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <Wand2 className="w-8 h-8 text-indigo-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Terminology Analysis in Progress</h3>
          <p className="text-gray-600 text-center max-w-md">
            Please wait while we extract and analyze business terms from your documents...
          </p>
          <div className="mt-4 flex space-x-1">
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      );
    }

    // Show informational card - Terminology generates automatically after synthesis
    return (
      <div className="text-center py-12 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl border border-indigo-200 shadow-sm">
        <div className="max-w-lg mx-auto px-6">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Info className="w-10 h-10 text-indigo-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Automated Terminology Analysis</h3>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Terminology analysis runs automatically once document analysis and process synthesis are complete. Business terms are extracted and analyzed as part of the workflow — before the SOP is generated, so approved terms flow into it.
          </p>
          <div className="bg-white/80 backdrop-blur-sm rounded-lg p-5 border border-indigo-200 shadow-sm">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 text-indigo-600" />
                </div>
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-800 mb-1">Analysis Workflow</p>
                <p className="text-sm text-gray-600">
                  Once your documents are analyzed and process synthesis is complete, terminology extraction begins automatically. No manual action needed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const allTerms = getFilteredAndSortedTerms(fullTerminologyData.enhancedTerms);

  return (
    <div className="space-y-6">
      {/* Enhanced Header with Stats */}

      <div className="bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 rounded-2xl border border-indigo-200/50 p-8 shadow-lg">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <h2 className="text-3xl font-bold text-gray-900">
                Project Terminology
              </h2>
              <HelpTooltip
                title="Terminology Extraction & Normalization"
                content={
                  <div className="space-y-2">
                    <p>Jarvis automatically extracts business terms from all uploaded documents, then normalizes them into a canonical glossary:</p>
                    <ul className="list-disc pl-4 space-y-1">
                      <li><strong>Cross-document matching</strong> — identifies when different documents use different names for the same concept</li>
                      <li><strong>Auto-approval</strong> — high-confidence matches are approved automatically, flagging only ambiguous terms for review</li>
                      <li><strong>Definition synthesis</strong> — merges definitions from multiple sources into authoritative entries</li>
                    </ul>
                    <p className="text-gray-500 italic">This builds the shared vocabulary that drives consistent SOP generation and stakeholder communication.</p>
                  </div>
                }
                variant="ai-explainer"
                iconSize={18}
              />
            </div>
            <p className="text-gray-600 text-lg">
              Manage and organize business terms extracted from your documents
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={handleAnalyzeTerminology}
              disabled={localAnalysisLoading || matchingInProgress}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              {localAnalysisLoading || matchingInProgress ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {matchingInProgress ? 'Matching Templates...' : 'Analyzing...'}
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Re-analyze
                </>
              )}
            </button>
          </div>
        </div>

        {(localAnalysisLoading || matchingInProgress || realTimeStats.uniqueTerms > 0) && (
          <div className={`mt-6 rounded-2xl p-6 border ${localAnalysisLoading
            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
            : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                {localAnalysisLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <h4 className="font-bold text-blue-900">
                      {matchingInProgress ? 'Template Matching in Progress...' : 'Analysis in Progress...'}
                    </h4>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <h4 className="font-bold text-green-900">Analysis Complete</h4>
                    <span className="text-xs text-green-700 ml-2">
                      ({realTimeStats.uniqueTerms} unique terms found)
                    </span>
                  </>
                )}
              </div>

              <button
                onClick={() => setShowRealTimeLog(!showRealTimeLog)}
                className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${localAnalysisLoading
                  ? 'text-blue-600 hover:text-blue-800 hover:bg-blue-100'
                  : 'text-green-600 hover:text-green-800 hover:bg-green-100'
                  }`}
              >
                <Eye className="w-4 h-4 mr-2" />
                {showRealTimeLog ? 'Hide' : 'Show'} Activity Log
              </button>
            </div>

            {/* Real-time Stats Grid */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="text-center bg-white/70 rounded-xl p-4 shadow-sm">
                <div className="text-2xl font-bold text-blue-600">{realTimeStats.uniqueTerms}</div>
                <div className="text-sm text-blue-700 font-medium">Terms Found</div>
              </div>
              <div className="text-center bg-white/70 rounded-xl p-4 shadow-sm">
                <div className="text-2xl font-bold text-purple-600">{realTimeStats.termsMatched}</div>
                <div className="text-sm text-purple-700 font-medium">Matched</div>
              </div>
              <div className="text-center bg-white/70 rounded-xl p-4 shadow-sm">
                <div className="text-2xl font-bold text-green-600">{realTimeStats.autoApproved}</div>
                <div className="text-sm text-green-700 font-medium">Auto-approved</div>
              </div>
              <div className="text-center bg-white/70 rounded-xl p-4 shadow-sm">
                <div className="text-2xl font-bold text-yellow-600">{realTimeStats.needsReview}</div>
                <div className="text-sm text-yellow-700 font-medium">Need Review</div>
              </div>
            </div>

            {/* Current Status Message - NEW ADDITION */}
            {localAnalysisLoading && realtimeLog.length > 0 && (
              <div className="bg-white/80 rounded-xl p-4 mb-4 border border-blue-100">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-blue-800">
                    {realtimeLog[realtimeLog.length - 1]?.message || 'Processing...'}
                  </span>
                  <span className="text-xs text-blue-600 ml-auto">
                    {realtimeLog[realtimeLog.length - 1]?.timestamp}
                  </span>
                </div>
              </div>
            )}

            {/* Template Matching Progress Indicator - ENHANCED */}
            {matchingInProgress && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                <div className="flex items-center space-x-3">
                  <RefreshCw className="h-4 w-4 text-yellow-600 animate-spin" />
                  <span className="text-sm font-medium text-yellow-800">
                    Template matching in progress... ({realTimeStats.termsMatched} terms processed)
                  </span>
                </div>
              </div>
            )}

            {/* Real-time Activity Log - ENHANCED VERSION */}
            {showRealTimeLog && (
              <div className="bg-white/80 rounded-xl border border-gray-200 shadow-sm">
                <div className={`p-3 border-b rounded-t-xl ${localAnalysisLoading ? 'bg-blue-50/50 border-blue-100' : 'bg-green-50/50 border-green-100'
                  }`}>
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-700">Real-time Activity</h5>
                    <span className="text-xs text-gray-500">{realtimeLog.length} events</span>
                  </div>
                </div>
                <div className="h-48 overflow-y-auto p-3">
                  {realtimeLog.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="text-gray-400 mb-2">
                          <Clock className="h-8 w-8 mx-auto" />
                        </div>
                        <div className="text-gray-500 text-sm">Waiting for analysis to start...</div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {realtimeLog.slice(-20).map((entry, index) => (
                        <div
                          key={entry.id}
                          className={`flex items-start space-x-3 text-sm p-2 rounded-lg transition-colors ${index === realtimeLog.slice(-20).length - 1 ? 'bg-blue-50' : 'hover:bg-gray-50'
                            }`}
                        >
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${entry.type === 'error' ? 'bg-red-500' :
                            entry.type === 'warning' ? 'bg-yellow-500' :
                              entry.type === 'system' ? 'bg-blue-500' :
                                entry.type === 'term_matched' ? 'bg-purple-500' :
                                  'bg-green-500'
                            }`}></div>
                          <div className="flex-1 min-w-0">
                            <span className={`font-medium block ${entry.type === 'error' ? 'text-red-700' :
                              entry.type === 'warning' ? 'text-yellow-700' :
                                entry.type === 'system' ? 'text-blue-700' :
                                  entry.type === 'term_matched' ? 'text-purple-700' :
                                    'text-gray-700'
                              }`}>
                              {entry.message}
                            </span>
                            <span className="text-xs text-gray-400 font-mono">
                              {entry.timestamp}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}


        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Unique Terms</p>
                <p className="text-2xl font-bold text-gray-900">{fullTerminologyData.summary.totalTerms}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-gray-900">{fullTerminologyData.summary.knownTerms}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl flex items-center justify-center">
                <Info className="w-6 h-6 text-amber-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Review</p>
                <p className="text-2xl font-bold text-gray-900">{fullTerminologyData.summary.unknownAcronyms}</p>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Enhanced Controls */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
            {/* Search and Filters */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search terms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-64"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending Review</option>
                <option value="review">Under Review</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSelectAll}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {selectedTerms.size === allTerms.length && allTerms.length > 0 ? 'Deselect All' : 'Select All'}
              </button>

              <ToolbarDropdown
                label={isDownloading ? 'Downloading...' : 'Export'}
                icon={<Download className="w-4 h-4" />}
                dropdownPosition="bottom-right"
                items={[
                  {
                    key: 'csv',
                    label: 'Download as CSV',
                    icon: <FileText className="w-4 h-4" />,
                    onClick: () => handleDownloadTerminology('csv'),
                    disabled: isDownloading,
                  },
                  {
                    key: 'xlsx',
                    label: 'Download as Excel',
                    icon: <FileText className="w-4 h-4" />,
                    onClick: () => handleDownloadTerminology('xlsx'),
                    disabled: isDownloading,
                  },
                ]}
              />

              <label className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer disabled:opacity-50">
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? 'Uploading...' : 'Import'}
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleUploadTerminology}
                  className="hidden"
                  disabled={isUploading}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Sorting Controls */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Sort by:</span>
            <button
              onClick={() => handleSort('term')}
              className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium transition-colors ${sortField === 'term'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
            >
              Term
              {sortField === 'term' && (
                sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
              )}
            </button>
            <button
              onClick={() => handleSort('confidence')}
              className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium transition-colors ${sortField === 'confidence'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
            >
              Confidence
              {sortField === 'confidence' && (
                sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
              )}
            </button>
            <button
              onClick={() => handleSort('status')}
              className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium transition-colors ${sortField === 'status'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
            >
              Status
              {sortField === 'status' && (
                sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
              )}
            </button>
            <div className="flex-1 text-right">
              <span className="text-sm text-gray-500">
                Showing {allTerms.length} of {fullTerminologyData.summary.totalTerms} terms
              </span>
            </div>
          </div>
        </div>

        {/* Terms List */}
        <div className="p-6">
          {allTerms.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Terms Found</h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No terminology has been extracted yet.'}
              </p>
            </div>
          ) : (
            <div className="grid gap-6">
              {allTerms.map((term) => (
                <ModernTermCard
                  key={term.id}
                  term={term}
                  editingTerm={editingTerm}
                  editedDefinition={editedDefinition}
                  setEditedDefinition={setEditedDefinition}
                  editedTermName={editedTermName}
                  setEditedTermName={setEditedTermName}
                  editedContext={editedContext}
                  setEditedContext={setEditedContext}
                  onEdit={handleEditTerm}
                  onSave={handleSaveTerm}
                  onCancel={handleCancelEdit}
                  onApprove={handleApproveTerm}
                  onDelete={handleDeleteTerm}
                  isSelected={selectedTerms.has(term.id)}
                  onSelect={handleSelectTerm}
                  isSaving={isSavingTerm && editingTerm === term.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Merge Modal */}
      {isMergeModalOpen && (
        <MergeTermsModal
          isOpen={isMergeModalOpen}
          onClose={() => setIsMergeModalOpen(false)}
          selectedTerms={getSelectedTermsData()}
          projectId={effectiveProjectId}
          onMergeComplete={handleMergeComplete}
        />
      )}

      {/* Floating Action Bar - Shows when terms are selected */}
      {selectedTerms.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-2xl border border-indigo-700">
              <div className="px-6 py-4 flex items-center justify-between">
                {/* Selection Info */}
                <div className="flex items-center text-white">
                  <div className="flex items-center justify-center w-8 h-8 bg-white/20 rounded-full mr-3">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {selectedTerms.size} {selectedTerms.size === 1 ? 'term' : 'terms'} selected
                    </p>
                    <p className="text-xs text-indigo-100">
                      Choose an action below
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleMergeTerms}
                    disabled={selectedTerms.size < 2}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Merge Terms ({selectedTerms.size})
                  </button>

                  <button
                    onClick={handleBulkApprove}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all shadow-lg disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve ({selectedTerms.size})
                  </button>

                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all shadow-lg disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete ({selectedTerms.size})
                  </button>

                  {/* Clear Selection */}
                  <button
                    onClick={() => setSelectedTerms(new Set())}
                    className="inline-flex items-center justify-center w-9 h-9 text-white hover:bg-white/10 rounded-lg transition-all"
                    title="Clear selection"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminologyAnalysis;